(function() {
  // Stale fallback: inventory baked into the page by Liquid at render time
  // (<script class="variant-stock-data">). Only used when a live variant
  // fetch isn't available — this can be minutes/hours old, so prefer
  // fetchLiveVariant()/getLiveStock() wherever possible.
  function getStockForVariant(variantId) {
    let stock = Infinity;
    document.querySelectorAll('script.variant-stock-data').forEach(s => {
      try {
        const map = JSON.parse(s.textContent);
        if (map[variantId] != null) {
          stock = Math.min(stock, map[variantId]);
        }
      } catch (e) {}
    });
    return stock;
  }

  // Live, uncached fetch of a single variant's real inventory count via the
  // Storefront GraphQL API. NOTE: Shopify's public /products/{handle}.js
  // REST endpoint does NOT expose inventory_quantity at all (confirmed by
  // inspecting a real response — the field is simply absent), so an earlier
  // version of this file that read inventory_quantity from that endpoint
  // was silently always falling back to Infinity for every product, on
  // every store. GraphQL's quantityAvailable is the only public field that
  // actually carries the real number. Requires window.SFS_STOREFRONT_TOKEN /
  // window.SFS_STOREFRONT_API_URL to be set (see layout/theme.liquid) from
  // a Storefront API access token with the unauthenticated_read_product_
  // inventory scope, configured in Theme Settings.
  async function fetchLiveVariant(variantId) {
    if (!window.SFS_STOREFRONT_TOKEN || !window.SFS_STOREFRONT_API_URL) {
      console.warn('[InventoryCheck] Storefront API token not configured — falling back to stale stock data. Set it in Theme Settings.');
      return null;
    }
    try {
      const query = `query($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            availableForSale
            quantityAvailable
          }
        }
      }`;
      const res = await fetch(window.SFS_STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.SFS_STOREFRONT_TOKEN
        },
        body: JSON.stringify({
          query,
          variables: { id: `gid://shopify/ProductVariant/${variantId}` }
        }),
        cache: 'no-store'
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (json.errors) {
        console.warn('[InventoryCheck] Storefront API errors:', json.errors);
        return null;
      }
      return (json.data && json.data.node) || null;
    } catch (e) {
      console.warn('[InventoryCheck] live variant fetch failed:', e);
      return null;
    }
  }

  // Resolve a usable stock number from a live variant object. quantityAvailable
  // is null when inventory isn't tracked at all — Shopify itself won't block
  // the purchase on quantity in that case, so treat it as unlimited.
  // "Continue selling when out of stock" is deliberately NOT treated as
  // unlimited when inventory IS tracked: BYOB assembles a physical bucket
  // from physical units, so it enforces the real count even for products
  // Shopify would otherwise let oversell/backorder at checkout.
  function resolveStock(liveVariant, fallbackVariantId) {
    if (liveVariant) {
      return typeof liveVariant.quantityAvailable === 'number'
        ? liveVariant.quantityAvailable
        : Infinity;
    }
    return getStockForVariant(fallbackVariantId);
  }

  // Convenience: fetch + resolve in one call, for callers that don't already
  // have a freshly-fetched variant object on hand.
  async function getLiveStock(variantId) {
    const liveVariant = await fetchLiveVariant(variantId);
    return resolveStock(liveVariant, variantId);
  }

  // `includeBucket: false` is for verifying a bucket's OWN contents at
  // checkout time — there, the quantity being checked (requestedQty) IS the
  // in-progress bucket's count for this variant, so counting it again here
  // as "already committed" would compare that single item against itself
  // and always come up short. The BYOB "add one more" flow wants the
  // default (true): it's asking whether requestedQty NEW units fit on top
  // of what's already in the bucket.
  async function getCommittedQuantity(variantId, { includeBucket = true } = {}) {
    const cards = JSON.parse(localStorage.getItem('cards')) || [];
    const inBucket = includeBucket
      ? cards.filter(c => Number(c.id) === Number(variantId)).length
      : 0;

    let inCart = 0;
    try {
      const cart = await fetch(`/cart.js?_=${Date.now()}`, { cache: 'no-store' }).then(r => r.json());
      cart.items.forEach(item => {
        if (Number(item.variant_id) === Number(variantId)) {
          inCart += item.quantity;
        }
     const bundleProperty = (item.properties && item.properties._bundle_products)
                    || (item.properties && item.properties._contents);
if (bundleProperty) {
  const ids = String(bundleProperty).split(',').map(s => Number(s.trim()));
  const matches = ids.filter(id => id === Number(variantId)).length;
  inCart += matches * item.quantity;
}
      });
    } catch (e) {
      console.warn('[InventoryCheck] /cart.js fetch failed:', e);
    }

    return { inBucket, inCart, total: inBucket + inCart };
  }

  // Pass a freshly-fetched `liveVariant` (from fetchLiveVariant() above)
  // whenever you have one — it's what makes this a real-time check instead
  // of relying on page-load-time inventory. Falls back to the stale
  // baked-in value only when no live variant is given.
  async function canAddToInventory(variantId, requestedQty = 1, liveVariant = null, committedOptions = {}) {
    const stock = resolveStock(liveVariant, variantId);
    if (stock === Infinity) {
      return { ok: true, stock: Infinity, committed: 0, remaining: Infinity };
    }
    const { inBucket, inCart, total } = await getCommittedQuantity(variantId, committedOptions);
    const remaining = stock - total;
    return {
      ok: requestedQty <= remaining,
      stock,
      inBucket,
      inCart,
      committed: total,
      remaining
    };
  }

  window.InventoryCheck = {
    getStockForVariant,
    fetchLiveVariant,
    getLiveStock,
    getCommittedQuantity,
    canAddToInventory
  };

  console.log('[InventoryCheck] loaded');
})();
